const $ = jQuery;
const nbsp = '\u00A0';

module.exports = class {
	#apiBaseUrl = '/wp-json/lumeris';
	#headers;
	#statusCode;
	#linkIndex;
	#linkElement;
	#dateString;
	#fileSizeString;
	#$link;
	#$lastUpdated;

	constructor(linkIndex, linkElement) {
		this.#linkIndex = linkIndex ?? null;
		this.#linkElement = linkElement ?? null;

		this.#$link = $(linkElement);

		this.#dateString = this.#$link.attr('data-last-updated');
		this.#$link.removeAttr('data-last-updated');

		this.#fileSizeString = this.#$link.attr('data-file-size');
		this.#$link.removeAttr('data-file-size');

		this.removeExistingDateString();
		this.setLastUpdatedElement();
		this.init();

		this.bind();
	}

	async init() {
		if (this.$link.hasClass('coming-soon')) {
			return this.modifyToComingSoon();
		}

		if (this.dateString && this.fileSizeString) {
			return this.appendLastUpdatedDate();
		}

		this.#headers = await this.getHeaders();

		if (this.statusCode === 200) {
			this.appendLastUpdatedDate();
		}
	}

	bind() {
		if (this.$link.hasClass('coming-soon')) {
			this.$link.on('click', (event) => { this.onComingSoonLinkClick(event); });
		}
	}

	get linkIndex() {
		return this.#linkIndex;
	}

	get linkElement() {
		return this.#linkElement;
	}

	get $link() {
		return this.#$link;
	}

	get $lastUpdated() {
		return this.#$lastUpdated;
	}

	get apiBaseUrl() {
		return this.#apiBaseUrl;
	}

	get url() {
		return this.$link.attr('href');
	}

	get dateString() {
		return this.#dateString;
	}
	
	get fileSizeString() {
		return this.#fileSizeString;
	}

	get headers() {
		return this.#headers;
	}

	get statusCode() {
		return this.#statusCode;
	}

	get contentType() {
		return this.headers?.['content-type'] ?? null;
	}

	get contentLength() {
		return parseInt(this.headers?.['content-length'] ?? '0');
	}

	get fileSize() {
		const contentLength = this.contentLength;

		if (!contentLength) {
			return null;
		}

		const k = 1024;
		const m = k * 1024;
		const g = m * 1024;

		if (contentLength >= g) {
			return (contentLength / g).toFixed(2) + `${nbsp}GB`;
		}
		if (contentLength >= m) {
			return (contentLength / m).toFixed(2) + `${nbsp}MB`;
		}
		
		if (contentLength >= k) {
			return (contentLength / k).toFixed(2) + `${nbsp}KB`;
		}
		
		return `${contentLength}${nbsp}bytes`;
	}

	get lastModified() {
		return this.headers?.['last-modified'] ?? null;
	}

	get lastUpdatedDateArray() {
		const lastModified = this.lastModified;

		if (!lastModified) {
			return null;
		}

		const d = new Date(this.lastModified);

		return {
			day: `0${d.getDate()}`.slice(-2),
			month: `0${d.getMonth() + 1}`.slice(-2),
			year: d.getFullYear()
		};
	}

	get lastUpdatedLabelString() {
		switch (this.language) {
			case 'english' : return `Last${nbsp}Updated`;
			case 'spanish' : return 'Actualizado';
			case 'chinese' : return '更新日期';
		}

		return `Last${nbsp}Updated`;
	}

	get comingSoonLabelString() {
		switch (this.language) {
			case 'english' : return `Coming${nbsp}Soon`;
			case 'spanish' : return 'Próximamente';
			case 'chinese' : return '即將推出';
		}

		return `Coming${nbsp}Soon`;
	}

	get lastUpdatedDateString() {
		const luDate = this.lastUpdatedDateArray;

		if (!luDate) {
			return '';
		}

		switch (this.language) {
			case 'english' : return `${luDate.month}/${luDate.day}/${luDate.year}`;
			case 'spanish' : return `${luDate.day}/${luDate.month}/${luDate.year}`;
			case 'chinese' : return `${luDate.year}${nbsp}年${nbsp}${luDate.month}${nbsp}月${nbsp}${luDate.day}${nbsp}日`;
		}

		return `${luDate.month}/${luDate.day}/${luDate.year}`;
	}

	get language() {
		const href = this.$link.attr('href');

		if (href.includes('Spanish')) {
			return 'spanish';
		}

		if (href.includes('Chinese')) {
			return 'chinese';
		}

		return 'english';
	}

	set $link(val) {
		this.#$link = val;
	}

	set $lastUpdated(val) {
		this.#$lastUpdated = val;
	}

	set dateString(val) {
		this.#dateString = val;
	}
	
	set fileSizeString(val) {
		this.#fileSizeString = val;
	}

	set headers(val) {
		this.#headers = val;
	}

	set statusCode(val) {
		this.#statusCode = val;
	}

	async getHeaders() {
		if (this.headers) {
            return this.headers;
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}/v1/get-document-headers?url=${this.url}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                cache: 'no-cache'
            });

            const json = await response.json();

            if ((!json.code || (json.data && json.data.status !== 400)) && json.results && json.results.length > 0 && !json.results.message && !json.results.error_message) {
                this.headers = json.results;
            }

			this.statusCode = (json.code) ? json.code : 200;

            return json.results;
        } catch (error) {
            console.error(error);
            return [];
        }
	}

	setLastUpdatedElement() {
		const $embeddedLastUpdated = this.$link.find('.last-updated');
		const $appendedLastUpdated = this.$link.next('.last-updated');

		if ($embeddedLastUpdated.length !== 0) {
			this.$lastUpdated = $embeddedLastUpdated;
		} else if ($appendedLastUpdated.length !== 0) {
			this.$lastUpdated = $appendedLastUpdated;
		} else {
			this.$lastUpdated = $('<span class="last-updated"></span>');
			this.$link.after(this.$lastUpdated);
		}
	}

	appendLastUpdatedDate() {
		this.dateString = this.dateString ?? `(${this.lastUpdatedLabelString}:${nbsp}${this.lastUpdatedDateString})`;
		this.$lastUpdated.html(this.dateString);
	}

	modifyToComingSoon() {
		this.$link.addClass('coming-soon');

		this.setLastUpdatedElement();
		
		this.dateString = this.dateString ?? `(${this.comingSoonLabelString})`;
		this.$lastUpdated.html(`(${this.comingSoonLabelString})`);
	}

	removeExistingDateString() {
		const pattern = /\((Last Updated|Last updated|Actualizado|更新日期):\s.*?\)/g;
		const text = this.$link.html();
		this.$link.html(text.replace(pattern, '<span class="last-updated"></span>'));
	}

	onComingSoonLinkClick(event) {
		if (!(event.originalEvent instanceof Event)) {
			return null;
		}

        event.preventDefault();
		event.stopPropagation();
        alert('This document is not yet available and will be coming soon.');
	}
}